// External libraries
import { NON_WHITESPACE } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { getContent } from '../content';
import { ADDRESS_VALIDATION_ERROR } from './constants';

const CUSTOM_VALUE_MISSING_FIELDS = new Set([
  'cardCvv',
  'cardExpiry',
  'cardNumber',
  'signupTerms',
  'onboardOption',
]);
const FIELD_NAME_MAP = {
  bankAccountNumber: 'accountNumber',
  bankIban: 'iban',
  bankSortCode: 'sortCode',
  cardCvv: 'cvv',
  cardExpiry: 'expiry',
  phone: 'phoneNumber',
};

const isValueMissing = value => !value.length || !NON_WHITESPACE.test(value);

const getErrorCodeFromErrorData = errorData => {
  if (!Array.isArray(errorData)) {
    // `errorData` sometimes comes back as an object and it can have
    // indices as properties. In that case we'll grab the first error
    // or check to see if the `errorData` has a `code` property on it
    return errorData?.['0']?.code || errorData?.code || null;
  }

  const error = errorData.find(err => err?.code);
  return error?.code;
};

/**
 * @function getValidationMessage
 * @param {string} field | Field id
 * @param {string} value | Current value from the state
 * @param {boolean} isValid | Flag indicating if the field is valid
 * @returns {string}
 * @description Returns an error message to display for an invalid input
 */
const getFieldErrorKey = ({ field, isValid, value }) => {
  const isCustomValueMissing = CUSTOM_VALUE_MISSING_FIELDS.has(field);
  const fieldName = FIELD_NAME_MAP[field] || field;

  if (isValueMissing(value)) {
    return `fieldErrors.${isCustomValueMissing ? fieldName : 'value'}Missing`;
  }

  return `fieldErrors.${fieldName}Invalid`;
};

const getFieldError = data => {
  const { errorCode, field, isValid, value } = data;

  if (isValid) {
    return '';
  }

  const { CLIENT_ERROR_CODE, FIELDS } = ADDRESS_VALIDATION_ERROR;
  if (errorCode === CLIENT_ERROR_CODE && FIELDS.includes(field)) {
    return '';
  }

  // We only show the field error message for the password
  // input if the value is missing.
  if (!isValueMissing(value) && field === 'password') {
    return '';
  }

  if (field === 'cardNumber' && errorCode === 'invalidType') {
    const {
      api: { allowedCardTypes },
    } = appStore.getState();
    const supportedCardTypes = allowedCardTypes
      .map(cardType => getContent(`cardTypes.${cardType}`))
      .join(', ');

    return getContent('fieldErrors.cardTypeNotSupported', {
      supportedCardTypes,
    });
  }

  if (field === 'dateOfBirth') {
    const key = errorCode === 'tooYoung' ? 'dobLegalAge' : 'dobInvalidDate';
    return getContent(`fieldErrors.${key}`);
  }

  const contentKey = getFieldErrorKey(data);
  return getContent(contentKey);
};

export { getErrorCodeFromErrorData, getFieldError, getFieldErrorKey };
